import { DateTimePicker } from "@progress/kendo-react-dateinputs";
import _ from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { IConfig } from "../../Config";
import DataService from "../../data/DataService";
import Loader from "../Loader";

interface IProps {
    config: IConfig;
    onCancel: () => void;
    onUpdate: () => void;
}

const DefaultResultsPerPage = 9;
const DefaultHeadingLevel = "h1";
const DefaultLayoutStyle = "template";
const MaxPosts = 100;

const headingLevels = ["a", "div", "h1", "h2", "h3", "span"];
const layoutStyles = ["template", "grid", "list"];

const Settings = ({ config, onCancel, onUpdate }: IProps) => {
    const dataService: DataService = new DataService(config);

    const [headingLevel, setHeadingLevel] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [layoutStyle, setLayoutStyle] = useState("");
    const [summaryTemplates, setSummaryTemplates] = useState([]);
    const [summaryTemplateId, setSummaryTemplateId] = useState<number | null>(null);
    const [detailTemplates, setDetailTemplates] = useState([]);
    const [detailTemplateId, setDetailTemplateId] = useState<number | null>(null);
    const [maxPosts, setMaxPosts] = useState(9);
    const [postsAfter, setPostsAfter] = useState(new Date());
    const [showAuthor, setShowAuthor] = useState(true);
    const [showFeedName, setShowFeedName] = useState(false);
    const [showRecentPosts, setShowRecentPosts] = useState(true);
    const [categories, setCategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [defaultSearchTerm, setDefaultSearchTerm] = useState("");
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    const loadCategories = async () => {
        try {
            setIsLoading(true);
            const categories = (await dataService.getAnonymousCategoriesByFeeds(1, 1000))?.data?.items;
            setCategories(categories);
        } finally {
            setIsLoading(false);
        }
    };

    const loadTags = async () => {
        try {
            setIsLoading(true);
            const rawTags = await dataService.getTagsByFeeds(1, 1000);
            const tags = _.map(rawTags?.data?.items, (tag) => tag.tag);
            setTags(tags);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        (async () => {
            loadCategories();
            loadTags();
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                const settings = (await dataService.getSettings()).data;
                const summaryTemplatesData = (await dataService.getRenderTemplates("NewsSummary")).data || [];
                setSummaryTemplates(summaryTemplatesData);
                if (!summaryTemplatesData.some((template) => template.Id === settings.SummaryTemplateId) ) {
                    setLayoutStyle("grid");
                } else {
                    setLayoutStyle(settings.LayoutStyle || DefaultLayoutStyle);
                }
                setDetailTemplates((await dataService.getRenderTemplates("NewsDetail")).data || []);
                setHeadingLevel(settings.HeadingLevel || DefaultHeadingLevel);
                setSummaryTemplateId(settings.SummaryTemplateId);
                setDetailTemplateId(settings.DetailTemplateId);
                setMaxPosts(settings.MaxPosts || DefaultResultsPerPage);
                setPostsAfter(DateTime.fromISO(settings.PostsAfter).toJSDate());
                setShowAuthor(settings.ShowAuthor);
                setShowFeedName(settings.ShowFeedName);
                setShowRecentPosts(settings.ShowRecentPosts);
                setDefaultSearchTerm(settings.DefaultSearchTerm || "");
                setSelectedCategories(settings.SelectedCategories || []);
                setSelectedTags(settings.SelectedTags || []);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    const isValid = useCallback(() => {
        return !isNaN(maxPosts) && maxPosts > 0;
    }, [maxPosts]);

    const update = useCallback(async () => {
        try {
            setIsLoading(true);
            await dataService.updateSettings({
                DetailTemplateId: detailTemplateId == null ? parseInt(detailTemplates[0]?.Id, 10) : detailTemplateId,
                FeedIds: config.feedIds,
                HeadingLevel: headingLevel,
                IsAllFeeds: config.isAllFeeds,
                LayoutStyle: layoutStyle,
                MaxPosts: Math.max(maxPosts, 1),
                PostsAfter: postsAfter || new Date(),
                ShowAuthor: showAuthor,
                ShowFeedName: showFeedName,
                ShowRecentPosts: showRecentPosts,
                SummaryTemplateId: summaryTemplateId == null ? parseInt(summaryTemplates[0]?.Id, 10) : summaryTemplateId,
                defaultSearchTerm,
                selectedCategories,
                selectedTags,
            });
            setIsLoading(false);
        } catch (e) {
            setIsLoading(false);
        }
    }, [config, headingLevel, layoutStyle, maxPosts, postsAfter, showAuthor, showFeedName, showRecentPosts, summaryTemplates, detailTemplates, summaryTemplateId, detailTemplateId, defaultSearchTerm, selectedCategories, selectedTags]);

    return (
        <Loader isLoading={isLoading}>
            <div className="CMS_element">
                <div className="CMS_title CMS_T4"><em className="fa fa-cog" aria-hidden="true"></em> Settings</div>
                {isNaN(maxPosts) &&
                    <div className="validationError CMS_inlineAlerts CMS_error">Maximum number of posts to show per page is required</div>
                }
                <div className="CMS_e_row">
                    <div className="CMS_title CMS_T6">Post layout style</div>
                    <select
                        className="CMS_input"
                        value={layoutStyle}
                        onChange={(e) => {
                            const layoutStyle = e.currentTarget.value;
                            setLayoutStyle(layoutStyle);
                        }}
                    >
                        {_.map(layoutStyles, (style, index) =>
                        (<option
                            key={`layout-${index}`}
                            value={style}
                        >
                            {_.upperFirst(style)}
                        </option>),
                        )}
                    </select>
                </div>
                {layoutStyle === "template" ? (
                    <div className="CMS_e_row">
                        <div className="CMS_title CMS_T6">News Summary layout template</div>
                        <select
                            className="CMS_input"
                            value={summaryTemplateId}
                            onChange={(e) => {
                                let layoutTemplateId;
                                try {
                                    layoutTemplateId = parseInt(e.currentTarget.value, 10);
                                } catch {
                                    layoutTemplateId = null;
                                }
                                setSummaryTemplateId(layoutTemplateId);
                            }}
                        >
                            {_.map(summaryTemplates, (template, index) =>
                            (<option
                                key={`summary-template-${index + 1}`}
                                value={template.Id}
                            >
                                {_.upperFirst(template.Name)}
                            </option>),
                            )}
                        </select>
                    </div>
                ) : null}
                {layoutStyle === "template" ? (
                    <div className="CMS_e_row">
                        <div className="CMS_title CMS_T6">News Detail layout template</div>
                        <select
                            className="CMS_input"
                            value={detailTemplateId}
                            onChange={(e) => {
                                let detailTemplateId;
                                try {
                                    detailTemplateId = parseInt(e.currentTarget.value, 10);
                                } catch {
                                    detailTemplateId = null;
                                }
                                setDetailTemplateId(detailTemplateId);
                            }}
                        >
                            {_.map(detailTemplates, (template, index) =>
                            (<option
                                key={`detail-template-${index + 1}`}
                                value={template.Id}
                            >
                                {_.upperFirst(template.Name)}
                            </option>),
                            )}
                        </select>
                    </div>
                ) : null}
                <div className="CMS_e_row">
                    <div className="CMS_title CMS_T6">Maximum number of posts to show per page</div>
                    <input
                        type="number"
                        className="CMS_input"
                        max={MaxPosts}
                        min={1}
                        pattern="[0-9]*"
                        value={maxPosts}
                        onChange={(e) => {
                            const numString = e.target.value?.replace(/\D/g, "");
                            const num = Math.max(1, Math.min(MaxPosts, parseInt(numString, 10)));
                            setMaxPosts(num);
                        }}
                    />
                </div>
                <div className="CMS_e_row">
                    <div className="CMS_title CMS_T6">Don't show posts released before</div>
                    <DateTimePicker
                        width="100%"
                        className="k-datetime-container"
                        onChange={(e) => {
                            const postsAfter = e.target.value;
                            setPostsAfter(postsAfter);
                        }}
                        value={postsAfter}
                    />
                </div>
                <div className="CMS_e_row">
                    <div className="CMS_title CMS_T6">Show author</div>
                    <label className="CMS_checkbox">
                        <input
                            type="checkbox"
                            className="CMS_input"
                            checked={showAuthor}
                            onChange={(e) => setShowAuthor(e.target.checked)}
                        />
                        <span> <em className="fa" aria-hidden="true"></em>Show author</span>
                    </label>
                </div>
                <div className="CMS_e_row">
                    <div className="CMS_title CMS_T6">Show feed name</div>
                    <label className="CMS_checkbox">
                        <input
                            type="checkbox"
                            className="CMS_input"
                            checked={showFeedName}
                            onChange={(e) => setShowFeedName(e.target.checked)}
                        />
                        <span> <em className="fa" aria-hidden="true"></em>Show feed name</span>
                    </label>
                </div>
                <div className="CMS_e_row">
                    <div className="CMS_title CMS_T6">Show Recent Posts in article detail view</div>
                    <label className="CMS_checkbox">
                        <input
                            type="checkbox"
                            className="CMS_input"
                            checked={showRecentPosts}
                            onChange={(e) => setShowRecentPosts(e.target.checked)}
                        />
                        <span> <em className="fa" aria-hidden="true"></em>Show recent posts</span>
                    </label>
                </div>
                {layoutStyle !== "template" ? (
                    <div className="CMS_e_row">
                        <div className="CMS_title CMS_T6">Post heading level</div>
                        <select
                            className="CMS_input"
                            value={headingLevel}
                            onChange={(e) => {
                                const headingLevel = e.currentTarget.value;
                                setHeadingLevel(headingLevel);
                            }}
                        >
                            {_.map(headingLevels, (heading, index) =>
                            (<option
                                key={`heading-${index}`}
                                value={heading}
                            >
                                {_.upperFirst(heading || "")}
                            </option>),
                            )}
                        </select>
                    </div>
                ) : null}
                <div className="CMS_title CMS_T5">Default Filter Settings</div>
                <div className="CMS_e_row">
                    <div className="CMS_title CMS_T6">Search term</div>
                    <input
                        type="text"
                        className="CMS_input"
                        maxLength={50}
                        value={defaultSearchTerm}
                        onChange={(e) => setDefaultSearchTerm(e.target.value)}
                    />
                </div>
                <div className="CMS_e_row">
                    <div className="CMS_title CMS_T6">Categories</div>
                    {categories.length === 0 ? <span className="NR_filter_empty">There are currently no categories.</span> : null}
                    {_.map(categories, (category, index) => (
                        <button
                            className={`CMS_tag ${_.indexOf(selectedCategories, category.categoryId) < 0 ? "CMS_tagInactive" : ""}`}
                            key={`category-${index}`}
                            style={{ marginRight: "4px" }}
                            onClick={async (e) => {
                                e.preventDefault();
                                const selectedCategoriesClone = [...selectedCategories];
                                const i = _.indexOf(selectedCategoriesClone, category.categoryId);
                                if (i < 0) {
                                    selectedCategoriesClone.push(category.categoryId);
                                } else {
                                    selectedCategoriesClone.splice(i, 1);
                                }
                                setSelectedCategories(selectedCategoriesClone);
                            }}
                        >
                            {category.categoryName}
                        </button>
                    ))}
                </div>
                <div className="CMS_e_row">
                    <div className="CMS_title CMS_T6">Tags</div>
                    {tags.length === 0 ? <span className="NR_filter_empty">There are currently no tags.</span> : null}
                    {_.map(tags, (tag, index) => (
                        <button
                            className={`CMS_tag ${_.indexOf(selectedTags, tag) < 0 ? "CMS_tagInactive" : ""}`}
                            key={`tag-${index}`}
                            style={{ marginRight: "4px" }}
                            onClick={async (e) => {
                                e.preventDefault();
                                const selectedTagsClone = [...selectedTags];
                                const i = _.indexOf(selectedTags, tag);
                                if (i < 0) {
                                    selectedTagsClone.push(tag);
                                } else {
                                    selectedTagsClone.splice(i, 1);
                                }
                                setSelectedTags(selectedTagsClone);
                            }}
                        >
                            {tag}
                        </button>
                    ))}
                </div>
                <div className="CMS_buttonGroup CMS_bg-R">
                    <a
                        className={"CMS_button CMS_bAction" + (isValid() ? "" : " CMS_bVisualyDisabled")}
                        href="#"
                        onClick={async (e) => {
                            e.preventDefault();
                            if (isValid()) {
                                await update();
                                onUpdate();
                            }
                        }}
                        title={isValid() ? "" : "You cannot update the settings until all validation errors are resolved."}
                    >
                        Update
                    </a>&nbsp;
                    <a
                        className="CMS_button CMS_bCancel"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            onCancel();
                        }}
                    >
                        Cancel
                    </a>
                </div>
            </div>
        </Loader>
    );
};

export default Settings;
